/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Call and validate the implemented FastAPI identity-context operations.
 * @state-owner request-local transport and parsed response values
 * @allowed-imports API client public type and shared contracts
 * @forbidden React; query cache; role or permission inference
 * @public-api same-module auth hooks
 * @side-effects authenticated identity bootstrap, context read and personal-workspace provisioning
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key
 * @naming-rules docs/architecture/API_NAMING.md
 * @tests identity-context.test.ts
 * @security-boundary authenticated workspace client to FastAPI identity boundary
 * @data-classification profile and Organization membership metadata
 * @trust-model every response is parsed; membership data remains backend-authoritative
 */

import type { RequestOptions, VispoApiClient } from '@vispo/api-client';
import { type MeContextResponse, meContextResponseSchema } from '@vispo/contracts';

async function requestContext(
  apiClient: VispoApiClient,
  path: string,
  options?: RequestOptions,
): Promise<MeContextResponse> {
  const response = await apiClient.request<unknown>(path, options);
  return meContextResponseSchema.parse(response);
}

export function bootstrapMe(apiClient: VispoApiClient): Promise<MeContextResponse> {
  return requestContext(apiClient, '/api/v1/me/bootstrap', { method: 'POST' });
}

export function loadMeContext(apiClient: VispoApiClient): Promise<MeContextResponse> {
  return requestContext(apiClient, '/api/v1/me/context');
}

export function ensurePersonalWorkspace(
  apiClient: VispoApiClient,
  idempotencyKey: string,
): Promise<MeContextResponse> {
  return requestContext(apiClient, '/api/v1/me/personal-workspace', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}
